// R115 guest — the ONE Guest Recovery composer + activity timeline, shared by
// GuestRecoveryPage (the worklist) and the Calendar's Recovery action (the
// reservation inspector's form sheet), so "log what you did / close it with an
// outcome / reopen" reads and behaves the same in both places.
//
// Markup only (strings) + the client-side note rule. Each host binds its own
// clicks on the SAME data attributes the page always used:
//   [data-role="gr-note"]            the textarea (the host keeps the draft)
//   [data-act="log"|"close"|"reopen"] + [data-outcome]   the steps
//   .gr-draft / .gr-err              the draft hint / inline error
// Outcomes, labels, icons and the note-required rule come from
// lib/guestRecovery.mjs (the server applies the same rule — this is only the
// early, friendly check). Tokens + existing .btn / .pill classes only.
import { kebabHtml } from './Kebab';
import {
  OUTCOMES, OUTCOME_LABEL, OUTCOME_HELP, OUTCOME_ICON, STATE_LABEL, STATE_PILL, noteRequired, mergeLogs,
} from '../../../../lib/guestRecovery.mjs';
import type { RecoveryOutcome, RecoveryLogEntry } from '../../../../lib/guestRecovery.mjs';

export type RecoveryStateKey = 'open' | RecoveryOutcome;
type Esc = (s: unknown) => string;
type Fmt = (s: unknown) => string;

export interface ComposerOpts {
  closed: boolean;
  state: RecoveryStateKey;
  draft: string;
  err?: string;
  busy?: boolean;
  smoothedAt?: string | null;
  /** The line under the textarea while a draft is unsent. Omit to hide it. */
  draftHint?: string;
  esc: Esc;
  fmtT: Fmt;
}

/** The composer: textarea + draft hint + inline error + the step buttons + help line. */
export function recoveryComposerHtml(o: ComposerOpts): string {
  const esc = o.esc;
  const dis = o.busy ? 'disabled' : '';
  const outBtn = (out: RecoveryOutcome, primary = false) =>
    `<button type="button" class="btn ${primary ? 'btn-primary' : 'btn-secondary'} btn-sm gr-btn" data-act="close" data-outcome="${out}" title="${esc(`${OUTCOME_LABEL[out]} — ${OUTCOME_HELP[out]}${noteRequired(out) ? ' (needs a note)' : ''}`)}" ${dis}>${OUTCOME_ICON[out]} ${esc(OUTCOME_LABEL[out])}</button>`;
  const others = (OUTCOMES as RecoveryOutcome[]).filter(x => x !== o.state);
  return `
          <label class="gr-notelab">What did you do?
            <textarea class="input" data-role="gr-note" rows="3" maxlength="1000" placeholder="${o.closed ? 'Add a follow-up — the case stays closed…' : 'Called the guest, offered a late checkout, asked the owner about fans…'}">${esc(o.draft)}</textarea>
          </label>
          ${o.draftHint != null ? `<div class="gr-draft" ${o.draft.trim() ? '' : 'hidden'}>${esc(o.draftHint)}</div>` : ''}
          ${o.err ? `<div class="gr-err" role="alert">${esc(o.err)}</div>` : ''}
          <div class="gr-actions">
            <button type="button" class="btn btn-secondary btn-sm gr-btn" data-act="log" title="Add this step to the activity — ${o.closed ? 'the case stays closed' : 'does not close the case'}" ${dis}>＋ Log action</button>
            ${o.closed
              ? `<button type="button" class="btn btn-secondary btn-sm gr-btn" data-act="reopen" title="Back to Open — the outcome is cleared, the history stays" ${dis}>↩ Reopen</button>
                 ${kebabHtml(others.map(x => ({ icon: OUTCOME_ICON[x], label: OUTCOME_LABEL[x], title: `${OUTCOME_HELP[x]}${noteRequired(x) ? ' — needs a note' : ''}`, disabled: !!o.busy, attrs: { 'data-act': 'close', 'data-outcome': x } })), { title: 'Change outcome', label: 'Change outcome ⋯', tip: 'Close it with a different outcome', cls: 'gr-btn gr-kb' })}`
              : `${outBtn('smoothed', true)}${outBtn('partial')}${outBtn('unresolved')}${outBtn('blocked_owner')}`}
          </div>
          <div class="gr-help">${o.closed
            ? `Closed as <strong>${esc(STATE_LABEL[o.state])}</strong>${o.smoothedAt ? ` on ${esc(o.fmtT(o.smoothedAt))}` : ''}. Logging a step keeps it closed.`
            : `Partly resolved, Couldn't resolve and Blocked by owner need a note — what did we do, and why couldn't we fix it? Blocked by owner = the ${esc(OUTCOME_HELP.blocked_owner)}.`}</div>`;
}

// the case audit actions that duplicate a recovery_log entry (close / reopen)
const SYS_DUP = new Set(['smoothed', 'open', 'closed', 'reopened']);
const SOURCE_LABEL: Record<string, string> = { 'issues-board': 'from the Issues board', calendar: 'from the Calendar' };

/** The Activity timeline: the recovery log + (for a case) its system audit, oldest first. */
export function recoveryTimelineHtml(t: { recovery_log?: RecoveryLogEntry[]; system_log?: any[] } | null, o: { esc: Esc; fmtT: Fmt; emptyText?: string }): string {
  const esc = o.esc;
  const sys = (t && Array.isArray(t.system_log) ? t.system_log : [])
    .filter((e: any) => e && !SYS_DUP.has(String(e.action || '')))
    .map((e: any, i: number) => ({ id: `sys:${i}`, at: e.at, by: e.by || '', kind: 'system', text: `${String(e.action || '').replace(/^./, (c: string) => c.toUpperCase())}${e.note ? ` — ${e.note}` : ''}` }));
  const entries = mergeLogs(((t && t.recovery_log) || []) as RecoveryLogEntry[], sys as any[]) as any[];
  if (!entries.length) return `<div class="gr-tl-empty">${esc(o.emptyText || 'Nothing logged yet — write what you did below and tap ＋ Log action.')}</div>`;
  const chip = (e: any) => {
    if (e.kind === 'action') return '<span class="pill pill-blue">Action</span>';
    if (e.kind === 'legacy') return '<span class="pill pill-gray">Note</span>';
    if (e.kind === 'reopen') return '<span class="pill pill-red">Reopened</span>';
    if (e.kind === 'close') {
      const out = (OUTCOMES as string[]).includes(e.outcome) ? e.outcome as RecoveryOutcome : 'smoothed';
      return `<span class="pill ${STATE_PILL[out]}">${e.prev_outcome ? 'Outcome → ' : 'Closed · '}${esc(OUTCOME_LABEL[out])}</span>`;
    }
    return '';
  };
  return `<ol class="gr-tl">${entries.map(e => `
      <li class="gr-ev ${e.kind === 'system' ? 'gr-ev-sys' : ''}">
        <div class="gr-ev-meta">
          <span class="gr-ev-when">${o.fmtT(e.at)}</span>
          <span class="gr-ev-who">${esc(e.by_name || e.by || (e.kind === 'system' ? 'system' : 'team'))}</span>
          ${chip(e)}
          ${SOURCE_LABEL[e.source] ? `<span class="gr-ev-src">${SOURCE_LABEL[e.source]}</span>` : ''}
        </div>
        ${e.text ? `<div class="gr-ev-text">${esc(e.text)}</div>` : ''}
      </li>`).join('')}</ol>`;
}

/** The client's early note rule: a message to show inline, or null when the step may go. */
export function composerGuard(op: 'log' | 'close' | 'reopen', outcome: RecoveryOutcome | undefined, text: string): string | null {
  const t = String(text || '').trim();
  if (op === 'log' && !t) return 'Write what you did first, then tap ＋ Log action.';
  if (op === 'close' && outcome && noteRequired(outcome) && !t) return `A note is required for ${OUTCOME_LABEL[outcome]} — what did we do, and why couldn't we fix it?`;
  return null;
}

/** The composer + timeline rules (moved verbatim from GuestRecoveryPage's style block). */
export const RECOVERY_COMPOSER_CSS = `
        .gr-tl { list-style:none; margin:0; padding:0 0 0 14px; border-left:2px solid var(--border); display:flex; flex-direction:column; gap:10px; }
        .gr-ev { position:relative; }
        .gr-ev::before { content:''; position:absolute; left:-19px; top:5px; width:8px; height:8px; border-radius:50%; background:var(--accent); border:2px solid var(--bg-subtle); }
        .gr-ev.gr-ev-sys::before { background:var(--border-strong); }
        .gr-ev-meta { display:flex; align-items:center; gap:6px; flex-wrap:wrap; font-size:11px; color:var(--text-muted); }
        .gr-ev-when { font-variant-numeric:tabular-nums; color:var(--text-faint); }
        .gr-ev-who { font-weight:600; color:var(--text); }
        .gr-ev-src { color:var(--text-faint); }
        .gr-ev-text { font-size:12.5px; color:var(--text); margin-top:3px; white-space:pre-wrap; word-break:break-word; line-height:1.5; }
        .gr-ev-sys .gr-ev-who, .gr-ev-sys .gr-ev-text { color:var(--text-faint); font-weight:400; font-size:11.5px; }
        .gr-tl-empty { font-size:12px; color:var(--text-faint); }
        .gr-notelab { display:flex; flex-direction:column; gap:5px; font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:0.06em; color:var(--text-muted); }
        .gr-notelab .input { font-size:12.5px; text-transform:none; letter-spacing:0; font-weight:400; resize:vertical; }
        .gr-draft { font-size:11px; color:var(--text-faint); margin-top:-4px; }
        .gr-draft[hidden] { display:none; }
        .gr-err { font-size:12px; color:var(--danger-text); background:var(--danger-soft); border:1px solid var(--danger); border-radius:var(--radius-sm); padding:7px 10px; }
        .gr-actions { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }
        /* the shared kebab trigger, dressed as the row's other secondary buttons */
        .gr-actions .kb-btn.gr-kb { height:auto; min-width:0; padding:6px 10px; font-size:11px; font-weight:600; letter-spacing:0; line-height:inherit; background:var(--card); border:1px solid var(--border); color:var(--text); }
        .gr-actions .kb-btn.gr-kb:hover { background:var(--bg-subtle); border-color:var(--border-strong); }
        .gr-help { font-size:11px; color:var(--text-faint); line-height:1.5; }
        @media (max-width: 720px) {
          .gr-btn { min-height:36px; }
          .gr-actions .gr-btn { flex:1 1 calc(50% - 8px); justify-content:center; }
          .gr-notelab .input { font-size:16px; }
        }
`;
